/**
 * HR Employee Directory Component
 * Admin → HR Records → Employee Directory
 * 
 * Features:
 * - List all employees with filters
 * - Create/Edit employee records
 * - View attendance summary
 * - Status management (Active/Inactive/Terminated)
 * 
 * UI Design: Premium SaaS-style with modern visual hierarchy
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

import { useDebouncedSearch } from '../../../hooks/useDebouncedSearch';
// HR-DASH-PREMIUM (Jun 2026): shared premium-dashboard animations
// (fade-up entrances, ripple, skeleton shimmer, soft bg gradient)
import '../../../pages/admin/AdminHomePage.css';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Eye,
  Calendar,
  MapPin,
  Clock,
  Building,
  Mail,
  User,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Check,
  AlertCircle,
  TrendingUp,
  UserPlus,
  Download,
  Upload,
  Briefcase,
  ShieldCheck,
  Megaphone
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { RoleSelect } from './RoleSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { toast } from 'sonner';
import { EmployeeMasterFormSections, EmployeeMasterViewSections } from './EmployeeMasterFormSections';
import EmployeeImportModal from './EmployeeImportModal';
import HRClearanceModal from './HRClearanceModal';
import { buildEmptyMaster, pickMaster } from './employeeMasterFields';
import TodaysCelebrationsWidget from '../../celebrations/TodaysCelebrationsWidget';
import { formatDateTimeLocal, formatDateOnly } from '../../../utils/formatters';
import { ProbationPill, PROB_STATUS } from './probation/probationUi';
import { ProbationReviewsWidget } from './probation/ProbationReviewsWidget';

const API = process.env.REACT_APP_BACKEND_URL || '';

/* HR-DASH-PREMIUM (Jun 2026): smooth 0 → value count-up (~700ms, ease-out cubic) */
const useHRCountUp = (value, duration = 700) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return typeof value === 'number' ? display : (value ?? '—');
};

/* HR-DASH-PREMIUM: click ripple micro-interaction */
const hrRipple = (e) => {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const r = document.createElement('span');
  r.className = 'ahp-ripple';
  r.style.width = r.style.height = `${size}px`;
  r.style.left = `${e.clientX - rect.left - size / 2}px`;
  r.style.top = `${e.clientY - rect.top - size / 2}px`;
  el.appendChild(r);
  setTimeout(() => r.remove(), 650);
};

const hrGreeting = (d) => {
  const h = d.getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

// HR-FLICKER-001 (Aug 2026): these presentational components MUST stay at
// module scope — defining them inside the parent remounts them on every
// keystroke, replaying count-up/entrance animations (KPI flicker bug).
// ============================================================================
// UI COMPONENTS - Premium SaaS Style
// ============================================================================

// KPI Card — reference-design style: white card, soft icon container,
// label, large metric, supporting line, optional real-data sparkline.
const KPI_ACCENTS = {
  purple: 'bg-purple-100 text-purple-600',
  green: 'bg-emerald-100 text-emerald-600',
  orange: 'bg-orange-100 text-orange-600',
  violet: 'bg-violet-100 text-violet-600',
  blue: 'bg-blue-100 text-blue-600',
  rose: 'bg-rose-100 text-rose-600'
};
// HR-DASH-PREMIUM — gradient icon containers + glow (matches Admin Dashboard)
const KPI_GRADIENTS = {
  purple: 'from-purple-500 to-fuchsia-600 shadow-purple-500/25',
  green: 'from-emerald-500 to-green-600 shadow-emerald-500/25',
  orange: 'from-orange-400 to-amber-500 shadow-orange-500/25',
  violet: 'from-violet-500 to-purple-600 shadow-violet-500/25',
  blue: 'from-blue-500 to-indigo-600 shadow-blue-500/25',
  rose: 'from-rose-500 to-pink-600 shadow-rose-500/25'
};
const CARD_BORDERS = {
  purple: 'hover:border-purple-300', green: 'hover:border-emerald-300',
  orange: 'hover:border-orange-300', violet: 'hover:border-violet-300',
  blue: 'hover:border-blue-300', rose: 'hover:border-rose-300'
};
const SPARK_COLORS = {
  purple: '#9333ea', green: '#059669', orange: '#ea580c',
  violet: '#7c3aed', blue: '#2563eb', rose: '#e11d48'
};

const Sparkline = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 64},${22 - (v / max) * 18}`).join(' ');
  return (
    <svg width="64" height="24" viewBox="0 0 64 24" className="shrink-0" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
    </svg>
  );
};

const KPICard = ({ icon: Icon, value, label, accent, note, spark, testId, title, delay = 0, glass = false }) => {
  const n = useHRCountUp(value);
  return (
    <div data-testid={testId} title={title} style={{ animationDelay: `${delay}ms` }}
      className={`ahp-fade-up ${glass ? 'bg-white/80 backdrop-blur-md' : 'bg-white'} rounded-2xl border border-slate-200/70 p-4 shadow-[0_1px_3px_rgb(0,0,0,0.04)] transition-all duration-200 hover:shadow-[0_6px_20px_rgb(0,0,0,0.08)] hover:-translate-y-0.5`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 bg-gradient-to-br ${KPI_GRADIENTS[accent]} text-white shadow-md rounded-xl flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-xs text-slate-500 font-medium leading-tight">{label}</p>
      </div>
      <div className="flex items-end justify-between mt-2.5">
        <p className="text-3xl font-bold text-slate-900 tracking-tight leading-none tabular-nums">{n}</p>
        {spark && <Sparkline data={spark} color={SPARK_COLORS[accent]} />}
      </div>
      {note && <p className="text-[11px] text-slate-400 mt-1.5">{note}</p>}
    </div>
  );
};

// DASH-SIMPLIFY (Jul 22, 2026): OverviewTile + ActionTile removed with
// the HR Overview / Quick Actions panels.

// Team badge with stable color hash (HR-DASH-UX)
const TEAM_BADGE_PALETTE = [
  'bg-indigo-50 text-indigo-700 border-indigo-200',
  'bg-pink-50 text-pink-700 border-pink-200',
  'bg-orange-50 text-orange-700 border-orange-200',
  'bg-teal-50 text-teal-700 border-teal-200',
  'bg-sky-50 text-sky-700 border-sky-200',
  'bg-violet-50 text-violet-700 border-violet-200'
];
const TeamBadge = ({ team }) => {
  if (!team) return <span className="text-sm text-slate-400 italic">—</span>;
  const idx = [...team].reduce((a, c) => a + c.charCodeAt(0), 0) % TEAM_BADGE_PALETTE.length;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${TEAM_BADGE_PALETTE[idx]}`}>
      {team}
    </span>
  );
};

// Status badge - soft pill style
const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-amber-50 text-amber-700 border-amber-200',
    resigned: 'bg-orange-50 text-orange-600 border-orange-200',
    terminated: 'bg-red-50 text-red-600 border-red-200'
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.inactive} capitalize`}>
      {status}
    </span>
  );
};

// HR-IT-OFFBOARD-001 — IT Clearance status mirrored from the IT offboarding case
// IT-OFFBOARD-EVIDENCE-001: badge is clickable → opens the read-only clearance
// evidence record; a red dot flags open asset-recovery items awaiting HR.
const ITClearanceBadge = ({ clearance, onClick }) => {
  if (!clearance?.status) return null;
  const styles = {
    pending: 'bg-slate-100 text-slate-600 border-slate-200',
    in_progress: 'bg-blue-50 text-blue-600 border-blue-200',
    awaiting_asset_return: 'bg-amber-50 text-amber-700 border-amber-200',
    escalated_to_hr: 'bg-red-50 text-red-600 border-red-200',
    cleared: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  const labels = {
    pending: 'IT: Pending', in_progress: 'IT: In Progress',
    awaiting_asset_return: 'IT: Awaiting Assets', escalated_to_hr: 'IT: Escalated',
    cleared: 'IT: Cleared'
  };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(clearance.case_id); }}
      className={`inline-flex items-center gap-1 px-2 py-0.5 mt-1 rounded-full text-[10px] font-semibold border cursor-pointer hover:opacity-80 ${styles[clearance.status] || styles.pending}`}
      title={`IT Clearance: ${clearance.status.replace(/_/g, ' ')}${clearance.last_working_day ? ` · LWD ${formatDateOnly(clearance.last_working_day)}` : ''}${clearance.recovery_open ? ' · OPEN asset recovery item' : ''} — click to view record`}
      data-testid="it-clearance-badge"
    >
      {labels[clearance.status] || clearance.status}
      {clearance.recovery_open && <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-testid="it-clearance-recovery-dot" />}
    </button>
  );
};

// Location indicator - minimal
const LocationIndicator = ({ location }) => {
  const configs = {
    office: { icon: Building, label: 'Office' },
    remote: { icon: MapPin, label: 'Remote' },
    hybrid: { icon: Clock, label: 'Hybrid' }
  };
  
  const config = configs[location] || configs.office;
  const Icon = config.icon;

  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
      <Icon className="w-3.5 h-3.5" />
      <span>{config.label}</span>
    </span>
  );
};

// Avatar with photo support (falls back to role-colored initials)
const Avatar = ({ name, role, photo }) => {
  const roleColors = {
    admin: 'bg-purple-100 text-purple-700 ring-purple-200',
    processor: 'bg-blue-100 text-blue-700 ring-blue-200',
    title_search_team_leader: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
    tax_search_team_leader: 'bg-amber-100 text-amber-700 ring-amber-200',
    order_desk: 'bg-cyan-100 text-cyan-700 ring-cyan-200',
    default: 'bg-slate-100 text-slate-700 ring-slate-200'
  };

  if (photo) {
    return (
      <img
        src={photo}
        alt={name || 'Employee'}
        className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-200 flex-shrink-0"
      />
    );
  }

  const colorClass = roleColors[role] || roleColors.default;
  const initials = name?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className={`w-10 h-10 rounded-full ${colorClass} ring-2 flex items-center justify-center flex-shrink-0`}>
      <span className="text-sm font-semibold">{initials}</span>
    </div>
  );
};

const HREmployeeDirectory = ({ view = 'directory' }) => {
  // Data state
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);

  // Filter state
  const { searchInput: searchQuery, setSearchInput: setSearchQuery, debouncedValue: debouncedSearchQuery } = useDebouncedSearch('', 300);
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const location = useLocation();
  const [probationFilter, setProbationFilter] = useState(() => new URLSearchParams(location.search).get('probation_status') || ''); // PROBATION-REVIEW-001 P2/P5 (deep-link)
  // P5: dashboard tiles deep-link via ?probation_status=<bucket>; re-apply on every navigation (location.key changes even for the same URL)
  useEffect(() => {
    const v = new URLSearchParams(location.search).get('probation_status');
    if (v !== null) setProbationFilter(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  const [insights, setInsights] = useState(null);

  // Hover state for rows
  const [hoveredRow, setHoveredRow] = useState(null);

  // Options for dropdowns
  const [options, setOptions] = useState({
    roles: [],
    teams: [],
    timezones: [],
    managers: [],
    statuses: [],
    locations: []
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    full_name: '',
    work_email: '',
    role: '',
    team: '',
    manager_employee_id: '',
    date_of_joining: '',
    date_of_birth: '',
    work_location: 'office',
    employee_timezone: 'UTC',
    status: 'active',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
  const [limit, setLimit] = useState(() => {
    const saved = Number(sessionStorage.getItem('hrDirectoryRowsPerPage'));
    return ROWS_PER_PAGE_OPTIONS.includes(saved) ? saved : 25;
  });

  const handleRowsPerPageChange = (value) => {
    const next = Number(value);
    sessionStorage.setItem('hrDirectoryRowsPerPage', String(next));
    setLimit(next);
    setPage(1);
  };

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, '…', totalPages];
    if (page >= totalPages - 3) return [1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '…', page - 1, page, page + 1, '…', totalPages];
  };

  // Get auth token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch dropdown options
  const fetchOptions = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [roles, teams, timezones, managers, statuses, locations] = await Promise.all([
        axios.get(`${API}/api/hr/options/roles`, { headers }),
        axios.get(`${API}/api/hr/options/teams`, { headers }),
        axios.get(`${API}/api/hr/options/timezones`, { headers }),
        axios.get(`${API}/api/hr/options/managers`, { headers }),
        axios.get(`${API}/api/hr/options/statuses`, { headers }),
        axios.get(`${API}/api/hr/options/locations`, { headers })
      ]);

      setOptions({
        roles: roles.data.roles || [],
        teams: teams.data.teams || [],
        timezones: timezones.data.timezones || [],
        managers: managers.data.managers || [],
        statuses: statuses.data.statuses || [],
        locations: locations.data.locations || []
      });
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  }, []);

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (debouncedSearchQuery) params.append('search', debouncedSearchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (teamFilter) params.append('team', teamFilter);
      if (locationFilter) params.append('work_location', locationFilter);
      if (typeFilter) params.append('employment_type', typeFilter);
      if (probationFilter) params.append('probation_status', probationFilter);

      const response = await axios.get(`${API}/api/hr/employees?${params.toString()}`, { headers });
      
      setEmployees(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (err) {
      console.error('Error fetching employees:', err);
      if (err.response?.status === 403) {
        toast.error('Access denied. HR Records is admin-only.');
      } else {
        toast.error('Failed to load employees');
      }
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [page, limit, debouncedSearchQuery, statusFilter, teamFilter, locationFilter, typeFilter, probationFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/api/hr/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // HR-DASH-INSIGHTS — KPI extras + insights widget data
  const fetchInsights = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/api/hr/dashboard-insights`, { headers });
      setInsights(response.data);
    } catch (err) {
      console.error('Error fetching insights:', err);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
    fetchStats();
    fetchInsights();
  }, [fetchOptions, fetchStats, fetchInsights]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Reset to page 1 whenever a new search or filter is applied
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, statusFilter, teamFilter, locationFilter, typeFilter, probationFilter]);

  // EMP-WORKSPACE-001 — create/edit/view now live in the dedicated
  // Employee Workspace & Profile pages (the old modal is retired).
  const handleCreate = () => {
    if (hrReadOnly) return;
    navigate('/admin/hr/employees/new');
  };

  // HR-PERSONA-001: hr_specialist is read-only in the directory.
  const hrReadOnly = (() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}').role) === 'hr_specialist'; }
    catch { return false; }
  })();

  // TITLEFLOW-HR-EXPORT-RBAC-001: Directory export is Admin-only (hidden, not disabled, for every other role).
  const canExport = (() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}').role) === 'admin'; }
    catch { return false; }
  })();

  // HR-HOME-CARDS (Jul 22, 2026): HR Portal users navigate via dashboard
  // cards; their sidebar only has "HR Home". Admin keeps full sidebar.
  const isHRPortal = (() => {
    try { return ['hr_manager', 'hr_specialist', 'hr'].includes(JSON.parse(localStorage.getItem('user') || '{}').role); }
    catch { return false; }
  })();

  // HR-PORTAL-SPLIT (Jul 22, 2026): HR Portal splits this component into
  // an "HR Dashboard" landing page (KPIs + Quick Access) and a dedicated
  // "Employee Directory" page (search/filters/table/import/export).
  // Admin Portal keeps the combined 'full' experience unchanged.
  const effectiveView = !isHRPortal ? 'full' : view;
  const isDashboardView = effectiveView === 'dashboard';
  // HR-DASH-PREMIUM — live clock for the greeting pill (30s tick)
  const [hrNow, setHrNow] = useState(new Date());
  useEffect(() => {
    if (!isDashboardView) return undefined;
    const t = setInterval(() => setHrNow(new Date()), 30000);
    return () => clearInterval(t);
  }, [isDashboardView]);
  const showDirectoryContent = effectiveView !== 'dashboard';

  // EMP-MASTER-001 — import modal + filtered export
  const [importOpen, setImportOpen] = useState(false);
  const [clearanceCaseId, setClearanceCaseId] = useState(null);
  const navigate = useNavigate();

  // EMP-WORKSPACE-001 — resumable drafts (write roles only)
  const [drafts, setDrafts] = useState([]);
  const fetchDrafts = useCallback(async () => {
    if (hrReadOnly) return;
    try {
      const res = await axios.get(`${API}/api/hr/employee-drafts`, { headers: getAuthHeaders() });
      setDrafts(res.data.drafts || []);
    } catch { /* non-blocking */ }
  }, [hrReadOnly]);
  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);
  const deleteDraft = async (id) => {
    try {
      await axios.delete(`${API}/api/hr/employee-drafts/${id}`, { headers: getAuthHeaders() });
      toast.success('Draft deleted');
      fetchDrafts();
    } catch {
      toast.error('Failed to delete draft');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (probationFilter) params.set('probation_status', probationFilter);
      if (teamFilter) params.set('team', teamFilter);
      if (debouncedSearchQuery) params.set('search', debouncedSearchQuery);
      const res = await axios.get(`${API}/api/hr/employees/export?${params.toString()}`, {
        headers: getAuthHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Employee export downloaded');
    } catch {
      toast.error('Failed to export employees');
    }
  };

  // Open modal for edit
  const handleEdit = (employee) => {
    if (hrReadOnly) return;
    navigate(`/admin/hr/employees/${employee.employee_id}/edit`);
  };

  // Open the read-only profile page
  const handleView = (employee) => {
    navigate(`/admin/hr/employees/${employee.employee_id}`);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!editingEmployee && !formData.employee_id?.trim()) errors.employee_id = 'Employee ID is required';
    if (!formData.full_name?.trim()) errors.full_name = 'Full name is required';
    if (!formData.work_email?.trim()) errors.work_email = 'Work email is required';
    if (!formData.role) errors.role = 'Role is required';
    if (!formData.team) errors.team = 'Team is required';
    if (!formData.date_of_joining) errors.date_of_joining = 'Date of joining is required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.work_email && !emailRegex.test(formData.work_email)) {
      errors.work_email = 'Invalid email format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const headers = getAuthHeaders();

      if (editingEmployee) {
        // Update existing employee
        const updateData = { ...formData };
        delete updateData.work_email; // Email cannot be changed
        delete updateData.date_of_joining; // DoJ cannot be changed
        delete updateData.employee_id; // EMP-MANUAL-ID-001 — ID cannot be changed

        const res = await axios.put(
          `${API}/api/hr/employees/${editingEmployee.employee_id}`,
          updateData,
          { headers }
        );
        toast.success('Employee updated successfully');
        // Show team sync feedback if team was changed
        if (res.data?.team_sync) {
          const sync = res.data.team_sync;
          if (sync.synced) {
            toast.success('Team assignment synced to user account', { duration: 3000 });
          } else if (!sync.user_found) {
            toast.warning('Team updated but no matching user account found — leave routing not updated', { duration: 5000 });
          } else if (!sync.team_matched) {
            toast.warning('Team updated but no matching routing team found — leave routing not updated', { duration: 5000 });
          }
        }
      } else {
        // Create new employee
        const res = await axios.post(`${API}/api/hr/employees`, formData, { headers });
        toast.success('Employee created successfully');
        if (res.data?.team_sync) {
          const sync = res.data.team_sync;
          if (sync.synced) {
            toast.success('Team assignment synced to user account', { duration: 3000 });
          } else if (!sync.user_found) {
            toast.info('Employee created — no matching user account for team sync', { duration: 4000 });
          } else if (!sync.team_matched) {
            toast.warning('Employee created — team has no leave routing configuration', { duration: 5000 });
          }
        }
      }

      setIsModalOpen(false);
      fetchEmployees();
      fetchStats();
      fetchOptions(); // Refresh managers list
    } catch (err) {
      console.error('Submit error:', err);
      const message = err.response?.data?.detail || 'Failed to save employee';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // UI components moved to module scope — see HR-FLICKER-001 above.

  return (
    <div className={`max-w-[1560px] mx-auto space-y-5 ${isDashboardView ? 'ahp-root rounded-2xl px-1' : ''}`}>
      {/* Header — HR-DASH-HEADER (Jul 2026) + HR-DASH-PREMIUM greeting */}
      <div className="pt-5 pb-4 border-b border-slate-200/70 flex flex-wrap items-center justify-between gap-3 ahp-fade-up" data-testid="directory-header">
        <div>
          <p className="text-xs font-medium text-slate-400 mb-1" data-testid="directory-breadcrumb">
            HR <span className="mx-1 text-slate-300">/</span> <span className="text-slate-500">{isDashboardView ? 'Dashboard' : 'Employee Directory'}</span>
          </p>
          <h1 className="text-[22px] font-bold text-slate-900 tracking-tight leading-tight" data-testid="hr-dashboard-title">
            {isDashboardView ? <>{hrGreeting(hrNow)} <span role="img" aria-label="wave">👋</span></> : 'Employee Directory'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isDashboardView
              ? 'Here\u2019s a snapshot of your HR operations today.'
              : 'Manage employee records for payroll and compliance'}
          </p>
        </div>
        {isDashboardView && (
          <div className="flex items-center gap-2 text-sm text-slate-500 bg-white/70 backdrop-blur-md border border-slate-200/80 rounded-full px-4 py-2 shadow-sm" data-testid="hr-dashboard-clock">
            <Calendar className="w-4 h-4 text-slate-400" />
            {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional portal live clock */}
            <span>{hrNow.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span className="text-slate-300">·</span>
            {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional portal live clock */}
            <span className="font-medium text-slate-700 tabular-nums">{hrNow.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
          </div>
        )}
        {showDirectoryContent && (
        <div className="flex items-center gap-2">
          {canExport && (
            <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-employees-btn">
              <Download className="w-4 h-4" />
              Export
            </Button>
          )}
          {!hrReadOnly && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="import-employees-btn">
                <Upload className="w-4 h-4" />
                Import
              </Button>
              <Button onClick={handleCreate} className="gap-2 shadow-sm" data-testid="add-employee-btn">
                <Plus className="w-4 h-4" />
                Add Employee
              </Button>
            </>
          )}
        </div>
        )}
      </div>

      {/* EMP-WORKSPACE-001 — Draft Employees (resume / delete) */}
      {!hrReadOnly && showDirectoryContent && drafts.length > 0 && (
        <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4" data-testid="employee-drafts-section">
          <h3 className="text-sm font-semibold text-amber-800 mb-3">
            Draft Employees ({drafts.length})
          </h3>
          <div className="space-y-2">
            {drafts.map(d => (
              <div key={d.id} className="flex items-center justify-between bg-white border border-amber-100 rounded-lg px-4 py-2.5" data-testid={`employee-draft-${d.id}`}>
                <div className="text-sm">
                  <span className="font-medium text-slate-800">{d.data?.full_name || 'Unnamed Draft'}</span>
                  {d.data?.work_email && <span className="text-slate-400 ml-2">{d.data.work_email}</span>}
                  <span className="text-xs text-slate-400 ml-2">
                    Step {(d.current_step || 0) + 1} of 7 · Updated {d.updated_at ? formatDateTimeLocal(d.updated_at) : '—'} · by {d.created_by_name || '—'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7"
                    onClick={() => navigate(`/admin/hr/employees/new?draft=${d.id}`)}
                    data-testid={`resume-draft-${d.id}`}>
                    Resume
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => deleteDraft(d.id)}
                    data-testid={`delete-draft-${d.id}`}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Cards — reference design (HR-DASH-UX v2).
          HR-PORTAL-SPLIT: dashboard + admin views only */}
      {stats && !(isHRPortal && !isDashboardView) && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <KPICard glass={isDashboardView} icon={Users} value={stats.total_employees} label="Total Employees" accent="purple" testId="kpi-total" delay={60}
            title="All employee records: active, inactive and resigned" note="All records incl. inactive" />
          <KPICard glass={isDashboardView} icon={Check} value={stats.by_status?.active || 0} label="Active Employees" accent="green" testId="kpi-active" delay={120}
            note={`${(stats.by_status?.inactive || 0) + (stats.by_status?.resigned || 0) + (stats.by_status?.terminated || 0)} inactive/resigned`} />
          <KPICard glass={isDashboardView} icon={Calendar} value={insights?.on_leave_count ?? '—'} label="On Leave Today" accent="orange" testId="kpi-on-leave" delay={180}
            spark={insights?.on_leave_by_day} note="Last 14 days" />
          <KPICard glass={isDashboardView} icon={Clock} value={insights?.pending_approvals ?? '—'} label="Pending Approvals" accent="violet" testId="kpi-pending-approvals" delay={240}
            note="Awaiting manager or HR" />
          <KPICard glass={isDashboardView} icon={UserPlus} value={stats.recent_hires || 0} label="New Hires (30d)" accent="blue" testId="kpi-new-hires" delay={300}
            spark={insights?.hires_by_month} note="Hires · last 6 months" />
          <KPICard glass={isDashboardView} icon={AlertCircle} value={insights?.unlinked_count ?? '—'} label="Unlinked Accounts" accent="rose" testId="kpi-unlinked" delay={360}
            note="Active without user account" />
        </div>
      )}

      {/* PROBATION-REVIEW-001 P5 — compact Probation Reviews widget directly below the KPI row
          (HR Dashboard + Admin full view). Tiles deep-link to the filtered Employee Directory. */}
      {stats && !(isHRPortal && !isDashboardView) && (
        <div className="ahp-fade-up" style={{ animationDelay: '400ms' }}>
          <ProbationReviewsWidget onSelectBucket={effectiveView === 'full' ? (b) => { setProbationFilter(b); setPage(1); navigate(`/admin/hr/employees?probation_status=${b}`); } : undefined} />
        </div>
      )}

      {/* HR Quick Access — full-width primary navigation (HR Portal only).
          HR-QUICK-ACCESS (Jul 22, 2026): replaces the compact HR Modules
          widget; larger cards in a responsive 3–4 per row grid, per the
          user's enterprise landing-page mockup. */}
      {isDashboardView && (
        <div data-testid="hr-quick-access-section">
          <h3 className="text-base font-bold text-slate-900 mb-3">HR Quick Access</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[
              { icon: User, label: 'My Workspace', desc: 'Personal dashboard, profile, leave & documents', path: '/admin/workspace', accent: 'blue', testId: 'module-card-my-workspace' },
              { icon: Calendar, label: 'Leave Registry', desc: 'Holidays, policies, reports & adjustments', path: '/admin/hr/leave-registry', accent: 'violet', testId: 'module-card-leave-registry' },
              { icon: Users, label: 'Employee Directory', desc: 'Search, manage and administer employees', path: '/admin/hr/employees', accent: 'green', testId: 'module-card-employee-directory' },
              { icon: Eye, label: 'HR Audit Log', desc: 'View audit logs and track HR activities', path: '/admin/hr/audit-log', accent: 'orange', testId: 'module-card-audit-log' },
              ...(hrReadOnly ? [] : [{ icon: Megaphone, label: 'HR Announcements', desc: 'Shift timing & DST notices with acknowledgement tracking', path: '/admin/hr/announcements', accent: 'blue', testId: 'module-card-hr-announcements' }]),
              { icon: Clock, label: 'Break History', desc: 'Track breaks, downtime and work hours', path: '/hr/breaks/history', accent: 'purple', testId: 'module-card-break-history' },
              { icon: ShieldCheck, label: 'Policy Center', desc: 'Company policies, acknowledgments & compliance', path: '/admin/policies', accent: 'green', testId: 'module-card-policy-center' },
              { icon: Briefcase, label: 'Job Openings', desc: 'Manage job openings and requisitions', path: '/admin/recruitment/jobs', accent: 'rose', testId: 'module-card-job-openings' },
              { icon: UserPlus, label: 'Candidates', desc: 'View and manage all candidates', path: '/admin/recruitment/candidates', accent: 'blue', testId: 'module-card-candidates' },
              { icon: TrendingUp, label: 'Pipeline Board', desc: 'Track candidates across recruitment stages', path: '/admin/recruitment/kanban', accent: 'violet', testId: 'module-card-pipeline-board' },
            ].map((card, cardIdx) => (
              <button
                key={card.path}
                onClick={(e) => { hrRipple(e); navigate(card.path); }}
                data-testid={card.testId}
                style={{ animationDelay: `${420 + cardIdx * 50}ms` }}
                className={`ahp-fade-up ahp-rippleable group flex items-start gap-4 p-5 bg-white/80 backdrop-blur-md rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgb(0,0,0,0.04)] hover:shadow-xl hover:-translate-y-1.5 ${CARD_BORDERS[card.accent]} transition-all duration-200 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${KPI_GRADIENTS[card.accent]} text-white shadow-md rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:shadow-lg transition-all duration-200`}>
                  <card.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-slate-900 tracking-tight">{card.label}</p>
                    <ChevronRight className="w-4 h-4 text-blue-500 -translate-x-1.5 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-200 shrink-0" />
                  </div>
                  <p className="text-xs text-slate-500 leading-snug mt-1">{card.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Today's Celebrations — HR Dashboard widget (Aug 2026): birthdays &
          work anniversaries today with a replayable Celebrate overlay. */}
      {isDashboardView && <TodaysCelebrationsWidget />}

      {/* HR Insights + Upcoming Events — Admin Portal only; HR Portal
          shows these in the left sidebar (HR-PORTAL-SPLIT, Jul 22, 2026) */}
      {effectiveView === 'full' && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        <div className="contents">
          <div className="bg-white rounded-xl border border-slate-200/70 shadow-[0_1px_3px_rgb(0,0,0,0.04)] p-5 lg:col-span-3" data-testid="hr-insights-widget">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">HR Insights</h3>
            {!insights ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2.5" data-testid="insight-on-leave">
                  <span className="w-2 h-2 rounded-full bg-orange-400 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">{insights.on_leave_count} on leave today</p>
                    <p className="text-xs text-slate-400 truncate">
                      {insights.on_leave_today.length ? insights.on_leave_today.slice(0, 3).map(p => p.name).join(', ') : 'Everyone is in today'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5" data-testid="insight-pending-approvals">
                  <span className="w-2 h-2 rounded-full bg-violet-400 mt-1.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{insights.pending_approvals} pending approval{insights.pending_approvals === 1 ? '' : 's'}</p>
                    <button onClick={() => navigate('/admin/workspace?tab=leave&subtab=approvals')}
                      className="text-xs text-blue-600 hover:text-blue-800">Review requests →</button>
                  </div>
                </div>
                <div className="flex items-start gap-2.5" data-testid="insight-new-hires">
                  <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">{insights.new_hires_count} new hire{insights.new_hires_count === 1 ? '' : 's'} this month</p>
                    <p className="text-xs text-slate-400 truncate">
                      {insights.new_hires.length ? insights.new_hires.slice(0, 3).map(h => h.full_name).join(', ') : 'No recent hires'}
                    </p>
                  </div>
                </div>
                {insights.unlinked_count > 0 && (
                  <div className="flex items-start gap-2.5" data-testid="insight-unlinked">
                    <span className="w-2 h-2 rounded-full bg-rose-400 mt-1.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{insights.unlinked_count} unlinked account{insights.unlinked_count === 1 ? '' : 's'}</p>
                      <p className="text-xs text-slate-400">Employees without a linked user account</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Upcoming Events — real data: holidays, approved leaves, anniversaries */}
          <div className="bg-white rounded-xl border border-slate-200/70 shadow-[0_1px_3px_rgb(0,0,0,0.04)] p-5 lg:col-span-2" data-testid="upcoming-events-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-800">Upcoming Events</h3>
              <button onClick={() => navigate('/admin/workspace?tab=leave&subtab=availability')}
                className="text-xs font-medium text-blue-600 hover:text-blue-800" data-testid="events-view-calendar">
                View Calendar →
              </button>
            </div>
            {!insights ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : insights.upcoming_events?.length === 0 ? (
              <p className="text-sm text-slate-400">No events in the next 30 days</p>
            ) : (
              <div className="space-y-2.5">
                {(insights.upcoming_events || []).slice(0, 5).map((ev, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`event-row-${i}`}>
                    <div className="w-10 shrink-0 text-center bg-slate-50 border border-slate-100 rounded-lg py-1">
                      <p className="text-[10px] uppercase text-slate-400 leading-none">
                        {new Date(ev.date + 'T00:00:00').toLocaleString('en-US', { month: 'short' })}
                      </p>
                      <p className="text-sm font-bold text-slate-800 leading-tight">{ev.date.slice(8, 10)}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{ev.title}</p>
                      <p className="text-xs text-slate-400 truncate">{ev.subtitle}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Directory content — search, filters, table, pagination
          (HR-PORTAL-SPLIT: hidden on the HR Dashboard view) */}
      {showDirectoryContent && (
      <>
      {/* Unified Filter Bar */}
      <div className="bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-200 rounded-xl h-10"
              />
            </div>
          </div>
          
          {/* Divider */}
          <div className="hidden sm:block w-px h-8 bg-slate-200" />
          
          {/* Status Filter */}
          <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[140px] bg-white border-slate-200 rounded-xl h-10">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {options.statuses.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Team Filter */}
          <Select value={teamFilter || 'all'} onValueChange={(v) => { setTeamFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[160px] bg-white border-slate-200 rounded-xl h-10">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {options.teams.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Location Filter */}
          <Select value={locationFilter || 'all'} onValueChange={(v) => { setLocationFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[140px] bg-white border-slate-200 rounded-xl h-10" data-testid="location-filter">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {(options.locations?.length ? options.locations : [{ value: 'office', label: 'Office' }, { value: 'remote', label: 'Remote' }, { value: 'hybrid', label: 'Hybrid' }]).map(l => (
                <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Employment Type Filter */}
          <Select value={typeFilter || 'all'} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[160px] bg-white border-slate-200 rounded-xl h-10" data-testid="employment-type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {['Permanent', 'Probationary', 'Contract', 'Intern', 'Monthly Salary'].map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Probation Filter — PROBATION-REVIEW-001 P2 */}
          <Select value={probationFilter || 'all'} onValueChange={(v) => { setProbationFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[170px] bg-white border-slate-200 rounded-xl h-10" data-testid="probation-filter">
              <SelectValue placeholder="Probation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Probation: Any</SelectItem>
              <SelectItem value="tracked">All tracked</SelectItem>
              {['pending_verification', 'in_progress', 'due_14', 'overdue', 'awaiting_hr', 'extended', 'confirmed', 'reviewer_missing'].map(b => (
                <SelectItem key={b} value={b}>{b === 'due_14' ? 'Due within 14 days' : b === 'reviewer_missing' ? 'Reviewer flagged' : PROB_STATUS[b]?.label || b}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {(searchQuery || statusFilter || teamFilter || locationFilter || typeFilter || probationFilter) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setSearchQuery(''); setStatusFilter(''); setTeamFilter(''); setLocationFilter(''); setTypeFilter(''); setProbationFilter(''); setPage(1); }}
              className="text-slate-500 hover:text-slate-700"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-slate-500 flex items-center gap-2">
          Showing <span className="font-semibold text-slate-700">{employees.length}</span> of <span className="font-semibold text-slate-700">{total}</span> employees
          {loading && hasLoaded && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" data-testid="results-refresh-spinner" />}
        </p>
      </div>

      {/* Employee Table - Premium Style */}
      <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-opacity duration-200 ${loading && hasLoaded ? 'opacity-60' : ''}`}>
        {loading && !hasLoaded ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Loading employees...</p>
            </div>
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <h4 className="text-lg font-semibold text-slate-700 mb-2">No Employees Found</h4>
            <p className="text-slate-500 max-w-sm mx-auto mb-6">
              {searchQuery || statusFilter || teamFilter 
                ? 'Try adjusting your filters to find what you\'re looking for.'
                : 'Get started by adding your first employee record.'}
            </p>
            {!searchQuery && !statusFilter && !teamFilter && !hrReadOnly && (
              <Button onClick={handleCreate} className="gap-2">
                <Plus className="w-4 h-4" />
                Add First Employee
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-blue-600">
                  <th className="!bg-blue-600 !text-white px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Employee</th>
                  <th className="!bg-blue-600 !text-white px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Role / Team</th>
                  <th className="!bg-blue-600 !text-white px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Manager</th>
                  <th className="!bg-blue-600 !text-white px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Location</th>
                  <th className="!bg-blue-600 !text-white px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="!bg-blue-600 !text-white px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">Joined</th>
                  <th className="!bg-blue-600 !text-white px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employees.map((emp) => (
                  <tr 
                    key={emp.employee_id} 
                    className={`transition-colors cursor-pointer ${hoveredRow === emp.employee_id ? 'bg-slate-50' : 'bg-white'}`}
                    onMouseEnter={() => setHoveredRow(emp.employee_id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => handleView(emp)}
                  >
                    {/* Employee Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={emp.full_name} role={emp.role} photo={emp.photo_thumb} />
                        <div className="min-w-0" title={emp.work_email || ''}>
                          <p className="font-semibold text-slate-900 truncate">{emp.full_name}</p>
                          <p className="text-xs text-slate-400 font-mono mt-0.5">{emp.employee_id}</p>
                        </div>
                      </div>
                    </td>

                    {/* Role / Team */}
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs font-medium capitalize">
                        {emp.role?.replace(/_/g, ' ')}
                      </span>
                      <div className="mt-1.5"><TeamBadge team={emp.team} /></div>
                    </td>

                    {/* Manager */}
                    <td className="px-6 py-4">
                      {emp.manager_name ? (
                        <p className="text-sm text-slate-700">{emp.manager_name}</p>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Not Assigned</span>
                      )}
                    </td>

                    {/* Location */}
                    <td className="px-6 py-4">
                      <LocationIndicator location={emp.work_location} />
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start">
                        <StatusBadge status={emp.status} />
                        {emp.probation?.tracked && <span className="mt-1"><ProbationPill probation={emp.probation} compact testid={`probation-pill-${emp.employee_id}`} /></span>}
                        <ITClearanceBadge clearance={emp.it_clearance} onClick={(cid) => cid && setClearanceCaseId(cid)} />
                      </div>
                    </td>

                    {/* Joined */}
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600">{formatDateOnly(emp.date_of_joining)}</p>
                    </td>

                    {/* Actions - Show on hover */}
                    <td className="px-6 py-4 text-right">
                      <div className={`flex items-center justify-end gap-1 transition-opacity ${hoveredRow === emp.employee_id ? 'opacity-100' : 'opacity-0'}`}>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                          onClick={(e) => { e.stopPropagation(); handleView(emp); }}
                          data-testid={`view-employee-btn-${emp.employee_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!hrReadOnly && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 hover:bg-slate-100"
                            onClick={(e) => { e.stopPropagation(); handleEdit(emp); }}
                            data-testid={`edit-employee-btn-${emp.employee_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {!(loading && !hasLoaded) && total > 0 && (
          <div className="px-6 py-3.5 bg-slate-50/50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3" data-testid="directory-pagination-footer">
            {/* Left: record range */}
            <p className="text-sm text-slate-500 whitespace-nowrap" data-testid="pagination-record-count">
              Showing <span className="font-semibold text-slate-700">{(page - 1) * limit + 1}</span>–<span className="font-semibold text-slate-700">{Math.min(page * limit, total)}</span> of <span className="font-semibold text-slate-700">{total}</span> employees
            </p>

            {/* Centre: Previous / page numbers / Next */}
            <div className="flex items-center gap-1" data-testid="pagination-controls">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 gap-1 px-2.5"
                data-testid="pagination-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              {getPageNumbers().map((p, idx) => (
                p === '…' ? (
                  <span key={`ellipsis-${idx}`} className="px-1.5 text-sm text-slate-400 select-none">…</span>
                ) : (
                  <Button
                    key={p}
                    variant={p === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPage(p)}
                    className={`h-8 w-8 p-0 ${p === page ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                    data-testid={`pagination-page-${p}`}
                  >
                    {p}
                  </Button>
                )
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-8 gap-1 px-2.5"
                data-testid="pagination-next-btn"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Right: rows-per-page selector */}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-sm text-slate-500">Rows per page:</span>
              <Select value={String(limit)} onValueChange={handleRowsPerPageChange}>
                <SelectTrigger className="h-8 w-[76px]" data-testid="rows-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROWS_PER_PAGE_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={String(opt)} data-testid={`rows-per-page-${opt}`}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* Create/Edit/View Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {isViewMode 
                ? `Employee Details`
                : editingEmployee 
                  ? 'Edit Employee' 
                  : 'Add New Employee'}
            </DialogTitle>
          </DialogHeader>

          {isViewMode && editingEmployee ? (
            // View Mode
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                <Avatar name={editingEmployee.full_name} role={editingEmployee.role} />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{editingEmployee.full_name}</h3>
                  <p className="text-sm text-slate-500">{editingEmployee.employee_id} · {editingEmployee.work_email}</p>
                </div>
                <div className="ml-auto">
                  <StatusBadge status={editingEmployee.status} />
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Role</p>
                  <p className="text-sm font-medium text-slate-700 capitalize">{editingEmployee.role?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Team</p>
                  <p className="text-sm font-medium text-slate-700">{editingEmployee.team}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Manager</p>
                  <p className="text-sm font-medium text-slate-700">{editingEmployee.manager_name || <span className="text-slate-400 italic">Not Assigned</span>}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Date of Joining</p>
                  <p className="text-sm font-medium text-slate-700">{formatDateOnly(editingEmployee.date_of_joining)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Work Location</p>
                  <p className="text-sm font-medium text-slate-700 capitalize">{editingEmployee.work_location}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Timezone</p>
                  <p className="text-sm font-medium text-slate-700">{editingEmployee.employee_timezone}</p>
                </div>
              </div>

              {/* EMP-MASTER-001 — master database sections */}
              <EmployeeMasterViewSections employee={editingEmployee} />

              {/* Attendance Summary */}
              {editingEmployee.attendance_summary && (
                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-5 border border-slate-200/60">
                  <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    Attendance Summary ({editingEmployee.attendance_summary.month}/{editingEmployee.attendance_summary.year})
                  </h4>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-3xl font-bold text-blue-600">
                        {editingEmployee.attendance_summary.days_present}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Days Present</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-700">
                        {editingEmployee.attendance_summary.total_sessions}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Total Sessions</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-amber-600">
                        {editingEmployee.attendance_summary.exceptions?.missing_logout || 0}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Missing Logout</p>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setIsModalOpen(false)}>Close</Button>
                {!hrReadOnly && (
                  <Button onClick={() => { setIsViewMode(false); handleEdit(editingEmployee); }} data-testid="view-modal-edit-btn">
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Employee
                  </Button>
                )}
              </DialogFooter>
            </div>
          ) : (
            // Create/Edit Form
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {!editingEmployee && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Employee ID <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.employee_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                      placeholder="e.g. EMP-101"
                      data-testid="employee-id-input"
                      className={formErrors.employee_id ? 'border-red-500' : ''}
                    />
                    {formErrors.employee_id && <p className="text-xs text-red-500 mt-1">{formErrors.employee_id}</p>}
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="John Doe"
                    className={formErrors.full_name ? 'border-red-500' : ''}
                  />
                  {formErrors.full_name && <p className="text-xs text-red-500 mt-1">{formErrors.full_name}</p>}
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.work_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, work_email: e.target.value }))}
                    placeholder="john@company.com"
                    disabled={!!editingEmployee}
                    className={formErrors.work_email ? 'border-red-500' : ''}
                  />
                  {formErrors.work_email && <p className="text-xs text-red-500 mt-1">{formErrors.work_email}</p>}
                  {editingEmployee && <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>}
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <RoleSelect value={formData.role} onChange={(v) => setFormData(prev => ({ ...prev, role: v }))} options={options.roles} error={formErrors.role} testId="employee-role" />
                  {formErrors.role && <p className="text-xs text-red-500 mt-1">{formErrors.role}</p>}
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Team <span className="text-red-500">*</span>
                  </label>
                  <Select value={formData.team} onValueChange={(v) => setFormData(prev => ({ ...prev, team: v }))}>
                    <SelectTrigger className={formErrors.team ? 'border-red-500' : ''} data-testid="employee-team-select">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.teams.map(t => (
                        <SelectItem key={t.value} value={t.value} data-testid={`team-option-${t.value}`}>
                          <span className="flex items-center gap-2">
                            {t.label}
                            {t.has_routing && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Routed</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formErrors.team && <p className="text-xs text-red-500 mt-1">{formErrors.team}</p>}
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Manager</label>
                  <Select 
                    value={formData.manager_employee_id || 'none'} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, manager_employee_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select manager (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Manager</SelectItem>
                      {options.managers.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Date of Joining <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_of_joining: e.target.value }))}
                    disabled={!!editingEmployee}
                    className={formErrors.date_of_joining ? 'border-red-500' : ''}
                  />
                  {formErrors.date_of_joining && <p className="text-xs text-red-500 mt-1">{formErrors.date_of_joining}</p>}
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Date of Birth <span className="text-slate-400 text-xs">(optional — used for team birthday celebrations)</span>
                  </label>
                  <Input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                    data-testid="employee-dob-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Work Location</label>
                  <Select value={formData.work_location} onValueChange={(v) => setFormData(prev => ({ ...prev, work_location: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {options.locations.map(l => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Timezone</label>
                  <div className="h-10 flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700" data-testid="employee-timezone-readonly">
                    {formData.employee_timezone || 'Inherited (team / company default)'}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Unified with Attendance — managed under Shift &amp; Attendance.</p>
                </div>

                {editingEmployee && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Status</label>
                    <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {options.statuses.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Notes</label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                />
              </div>

              {/* EMP-MASTER-001 — master database sections */}
              <EmployeeMasterFormSections formData={formData} setFormData={setFormData} />

              <DialogFooter className="gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    editingEmployee ? 'Save Changes' : 'Create Employee'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* EMP-MASTER-001 — import modal */}
      {clearanceCaseId && (
        <HRClearanceModal caseId={clearanceCaseId} onClose={() => { setClearanceCaseId(null); fetchEmployees(); }} />
      )}
      <EmployeeImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => { fetchEmployees(); fetchStats(); }}
      />
    </div>
  );
};

export default HREmployeeDirectory;
